import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../Controls/Caption";
import { PasswordEdit } from "../Controls/PasswordEdit";
import { IContentBuilder } from "./IContentBuilder";

export class PasswordChangeBuilder implements IContentBuilder {
    private _mainElement: HTMLElement;

    private _oldPwdEdit: PasswordEdit;
    private _newPwdEdit: PasswordEdit;
    private _newPwdConfirmEdit: PasswordEdit;

    private _errorElement: HTMLElement;

    private static readonly _dialogClassNameConstant = "change-password-dialog";

    public get password(): string | null {
        if (!this._validate()) return null;
        return this._newPwdEdit.controlValue;
    }

    public get oldPassword(): string {
        return this._oldPwdEdit.controlValue;
    }

    public clear(): void {
        this._newPwdEdit.controlValue = "";
        this._newPwdConfirmEdit.controlValue = "";
        this._oldPwdEdit.controlValue = "";
    }

    public async setInvalidContent(content: IContentBuilder | null): Promise<void> {
        if (!content) {
            this._errorElement.innerHTML = "";
            return;
        }

        this._errorElement.classList.add(`${PasswordChangeBuilder._dialogClassNameConstant}__error-element--active`);
        this._errorElement.innerHTML = (await content.build()).outerHTML;
    }

    public setInvalid(text: string, edit?: PasswordChangeBuilderInputType): void {
        if (edit != undefined) {
            if (PasswordChangeBuilderInputType.confirmNewPassword == edit) this._newPwdConfirmEdit.setInvalid(null, text);
            if (PasswordChangeBuilderInputType.newPassword == edit) this._newPwdEdit.setInvalid(null, text);
            if (PasswordChangeBuilderInputType.oldPassword == edit) this._oldPwdEdit.setInvalid(null, text);
        } else {
            if (!this._newPwdConfirmEdit.controlValue) this._newPwdConfirmEdit.setInvalid(null, text);
            if (!this._newPwdEdit.controlValue) this._newPwdEdit.setInvalid(null, text);
            if (!this._oldPwdEdit.controlValue) this._oldPwdEdit.setInvalid(null, text);
        }
        this._errorElement.classList.add(`${PasswordChangeBuilder._dialogClassNameConstant}__error-element--active`);
        this._errorElement.textContent = text;
    }

    private _setInvalid(locKey: string, edit: PasswordEdit): void {
        edit.setInvalid(locKey);
        this._errorElement.classList.add(`${PasswordChangeBuilder._dialogClassNameConstant}__error-element--active`);
        Localization.addLocData(this._errorElement, locKey);
    }

    public setValid(): void {
        this._newPwdConfirmEdit.setValid();
        this._errorElement.classList.remove(`${PasswordChangeBuilder._dialogClassNameConstant}__error-element--active`);
        this._errorElement.innerHTML = "";
        this._errorElement.textContent = "";
    }

    constructor() {
        this._mainElement = createElement({ classes: PasswordChangeBuilder._dialogClassNameConstant });

        const pwdInputComponent = "password-input-component";
        this._oldPwdEdit = PasswordEdit.createWithEmptyRights(createElement({ classes: `${pwdInputComponent} ${pwdInputComponent}--first`, attrs: { id: "pwd-input", "data-captionlockey": "GUI-678535", "data-captiontop": "10", "data-captionleft": "20", "data-behaviour": "component" } })); // Původní heslo

        this._newPwdEdit = PasswordEdit.createWithEmptyRights(createElement({ classes: pwdInputComponent, attrs: { id: "pwd-input-new", "data-captionlockey": "GUI-321762", "data-captiontop": "60", "data-captionleft": "20", "data-behaviour": "component" } })); // Heslo
        this._newPwdConfirmEdit = PasswordEdit.createWithEmptyRights(createElement({ classes: pwdInputComponent, attrs: { id: "pwd-input-repeat", "data-captionlockey": "GUI-678497", "data-captiontop": "110", "data-captionleft": "20", "data-behaviour": "component" } })); // Opakování hesla
        this._errorElement = createElement({ classes: [`${PasswordChangeBuilder._dialogClassNameConstant}__error-element`, "errors"] });

        const inputGroups = createElement({ classes: `${PasswordChangeBuilder._dialogClassNameConstant}__input-groups` });
        inputGroups.appendChild(this._oldPwdEdit.element);
        inputGroups.appendChild(this._newPwdEdit.element);
        inputGroups.appendChild(this._newPwdConfirmEdit.element);

        this._mainElement.appendChild(inputGroups);
        this._mainElement.appendChild(this._errorElement);

        Caption.createCaptionForFirstChild(this._oldPwdEdit.element);
        Caption.createCaptionForFirstChild(this._newPwdEdit.element);
        Caption.createCaptionForFirstChild(this._newPwdConfirmEdit.element);
    }

    private _validate(): boolean {
        if (!this._newPwdEdit.controlValue || !this._newPwdConfirmEdit.controlValue || !this._oldPwdEdit.controlValue) {
            if (!this._newPwdEdit.controlValue) this._setInvalid("GUI-678503", this._newPwdEdit); // Musíte zadat heslo
            if (!this._newPwdConfirmEdit.controlValue) this._setInvalid("GUI-678503", this._newPwdConfirmEdit); // Musíte zadat heslo
            if (!this._oldPwdEdit.controlValue) this._setInvalid("GUI-678503", this._oldPwdEdit); // Musíte zadat heslo
            return false;
        }
        if (this._newPwdEdit.controlValue != this._newPwdConfirmEdit.controlValue) {
            this._setInvalid("GUI-678504", this._newPwdConfirmEdit); // Hesla musí být stejná
            this._setInvalid("GUI-678504", this._newPwdEdit); // Hesla musí být stejná
            return false;
        }

        this.setValid();
        return true;
    }

    public async build(): Promise<HTMLElement> {
        return this._mainElement;
    }
}

export enum PasswordChangeBuilderInputType {
    oldPassword,
    newPassword,
    confirmNewPassword,
}
